import { apiFunctionsEnum } from 'constants/api.constants';
import { StateScope } from 'constants/system-messages.constants';
import ApiService from './api.service';
import {
	beginContactIdsValidation,
	markMessageInactive,
	setContactIdsValidationIfCurrent,
	setCurrentMessage,
	setCurrentMessageLoading,
	setCurrentMessageRequestId,
	setDisabling,
	setError,
	setList,
	setLoading,
	setSaving,
} from 'state/reducers/system-messages.reducer';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import { buildPaginationQueryParams, setErrorMessage, type IPaginationParams } from 'utils/index';
import type {
	ICreateSystemMessagePayload,
	ISystemMessageContactIdsValidation,
	ISystemMessageDetail,
} from 'types/system-messages.types';

const apiService = ApiService.getInstance();

type FetchParams = Required<Pick<IPaginationParams, 'offset' | 'limit'>> &
	Pick<IPaginationParams, 'sortBy' | 'sortOrder' | 'filters'>;

type FetchMessageByIdParams = {
	id: number;
	companyId?: number;
};

type SystemMessagesGetState = {
	systemMessages: {
		currentMessageRequestId: number | null;
		contactValidationRequestId: number;
	};
};

export const fetchSystemMessages = (params: FetchParams) => {
	return async (dispatch: any) => {
		dispatch(setLoading({ scope: StateScope.Global, loading: true }));
		dispatch(setError({ exist: false }));
		try {
			const queryParams = buildPaginationQueryParams(params);
			const response = await apiService.execute(apiFunctionsEnum.GET_SYSTEM_MESSAGES, queryParams);
			dispatch(
				setList({
					scope: StateScope.Global,
					rows: response.data.result,
					total: response.data.pagination.total,
				})
			);
		} catch (error: any) {
			dispatch(
				setList({
					scope: StateScope.Global,
					rows: [],
					total: 0,
				})
			);
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
		} finally {
			dispatch(setLoading({ scope: StateScope.Global, loading: false }));
		}
	};
};

export const fetchSystemMessageById = ({ id, companyId }: FetchMessageByIdParams) => {
	return async (dispatch: any, getState: () => SystemMessagesGetState) => {
		const requestId = id;
		dispatch(setCurrentMessage(null));
		dispatch(setCurrentMessageRequestId(requestId));
		dispatch(setCurrentMessageLoading(true));
		try {
			const response = companyId
				? await apiService.execute(apiFunctionsEnum.GET_COMPANY_SYSTEM_MESSAGE_BY_ID, { companyId, id })
				: await apiService.execute(apiFunctionsEnum.GET_SYSTEM_MESSAGE_BY_ID, { id });
			if (getState().systemMessages.currentMessageRequestId !== requestId) {
				return;
			}
			dispatch(setCurrentMessage(response.data.result as ISystemMessageDetail));
		} catch (error: any) {
			if (getState().systemMessages.currentMessageRequestId === requestId) {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			}
		} finally {
			if (getState().systemMessages.currentMessageRequestId === requestId) {
				dispatch(setCurrentMessageLoading(false));
			}
		}
	};
};

export const createSystemMessage = (payload: ICreateSystemMessagePayload) => {
	return async (dispatch: any): Promise<{ success: boolean }> => {
		dispatch(setSaving(true));
		try {
			await apiService.execute(apiFunctionsEnum.CREATE_SYSTEM_MESSAGE, payload);
			dispatch(setSuccessMessage(true));
			return { success: true };
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return { success: false };
		} finally {
			dispatch(setSaving(false));
		}
	};
};

export const createSystemMessageForCompany = (payload: ICreateSystemMessagePayload, companyId: number) => {
	return async (dispatch: any): Promise<{ success: boolean }> => {
		dispatch(setSaving(true));
		try {
			await apiService.execute(apiFunctionsEnum.CREATE_SYSTEM_MESSAGE_COMPANY, {
				...payload,
				targetCompanyId: companyId,
			});
			dispatch(setSuccessMessage(true));
			return { success: true };
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return { success: false };
		} finally {
			dispatch(setSaving(false));
		}
	};
};

export const disableSystemMessage = (id: number) => {
	return async (dispatch: any): Promise<{ success: boolean }> => {
		dispatch(setDisabling(true));
		try {
			await apiService.execute(apiFunctionsEnum.DISABLE_SYSTEM_MESSAGE, { id });
			dispatch(setSuccessMessage(true));
			dispatch(markMessageInactive(id));
			return { success: true };
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return { success: false };
		} finally {
			dispatch(setDisabling(false));
		}
	};
};

export const validateContactIds = (contactIds: number[]) => {
	return async (dispatch: any, getState: () => SystemMessagesGetState) => {
		dispatch(beginContactIdsValidation());
		const requestId = getState().systemMessages.contactValidationRequestId;
		try {
			const response = await apiService.execute(apiFunctionsEnum.VALIDATE_CONTACT_IDS, { contactIds });
			const validation = response.data.result as ISystemMessageContactIdsValidation;
			dispatch(
				setContactIdsValidationIfCurrent({
					requestId,
					validation: {
						requestedIds: contactIds,
						valid: validation.valid,
						invalid: validation.invalid,
					},
				})
			);
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
		}
	};
};

export const fetchCompanySystemMessages = (params: FetchParams & { companyId: number }) => {
	return async (dispatch: any) => {
		dispatch(setLoading({ scope: StateScope.Company, loading: true }));
		dispatch(setError({ exist: false }));
		try {
			const queryParams = buildPaginationQueryParams(params);
			const response = await apiService.execute(apiFunctionsEnum.GET_COMPANY_SYSTEM_MESSAGES, {
				companyId: params.companyId,
				...queryParams,
			});
			dispatch(
				setList({
					scope: StateScope.Company,
					rows: response.data.result,
					total: response.data.pagination.total,
				})
			);
		} catch (error: any) {
			dispatch(
				setList({
					scope: StateScope.Company,
					rows: [],
					total: 0,
				})
			);
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
		} finally {
			dispatch(setLoading({ scope: StateScope.Company, loading: false }));
		}
	};
};
